// @ts-check
// File: app/src/utils/coldWarmup.js
//
// Longer warm-up in the cold (issue #76). The Khronos Timer carries a
// temperature sensor and reports it in its heartbeat while a session runs. When
// the first reading during the warm-up is cold, the coach offers ONE change:
// a few more minutes of warm-up, put on the timed warm-up step still to come
// (the current one included, so the time already served counts). When no such
// step is left, a short easy-cardio step is added straight after the warm-up.
//
// Pure: never mutates the plan, no React.

import { isRecoveryType, isTimedUnit } from "./planNodeUtils";
import { toSeconds } from "./time";

/** @typedef {import("../types/plan").PlanNode} PlanNode */
/** @typedef {import("../types/plan").QueueItem} QueueItem */

/** At or above this (°C) the warm-up is left alone. */
export const COLD_BELOW_C = 10;
/** At or below this (°C) the extra time grows. */
const FREEZING_C = 0;

/**
 * Extra warm-up seconds for a reading, or 0 when it isn't cold.
 *
 * @param {number | null | undefined} tempC
 */
export const extraWarmupSec = (tempC) => {
  if (typeof tempC !== "number" || !Number.isFinite(tempC)) return 0;
  if (tempC >= COLD_BELOW_C) return 0;
  return tempC <= FREEZING_C ? 300 : 180;
};

const isWarmupType = (type) => String(type || "").toLowerCase() === "warmup";

/**
 * A root-level block that is the warm-up: a Warmup step, or a block whose
 * label says so or whose steps are all Warmup.
 *
 * @param {PlanNode | undefined} node
 */
const isWarmupBlock = (node) => {
  if (!node) return false;
  if (isWarmupType(node.type)) return true;
  if (!Array.isArray(node.children)) return false;
  if (/warm/i.test(String(node.label || ""))) return true;
  const leaves = [];
  const walk = (n) => {
    if (Array.isArray(n.children)) n.children.forEach(walk);
    else if (!isRecoveryType(n.type)) leaves.push(n);
  };
  walk(node);
  return leaves.length > 0 && leaves.every((l) => isWarmupType(l.type));
};

/** @param {PlanNode} plan */
const clone = (plan) => JSON.parse(JSON.stringify(plan));

/**
 * @param {PlanNode} root
 * @param {ReadonlyArray<string | number>} path
 * @returns {any}
 */
const nodeAt = (root, path) => {
  /** @type {any} */
  let node = root;
  for (const key of path) {
    if (node == null) return undefined;
    node = node[key];
  }
  return node;
};

/** "3 min", or "2:30 min" when it isn't whole minutes. */
const minutesText = (sec) => {
  const s = Math.round(sec);
  if (s % 60 === 0) return `${s / 60} min`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")} min`;
};

/**
 * The cold warm-up change for where the session is now, or null when there is
 * nothing to offer: not cold, no warm-up, or the warm-up is already behind.
 *
 * @param {PlanNode} plan
 * @param {{ queue: QueueItem[], currentIndex: number, tempC: number }} ctx
 * @returns {{ plan: PlanNode, addSec: number, text: string } | null}
 */
export const proposeColdWarmup = (plan, { queue, currentIndex, tempC }) => {
  const addSec = extraWarmupSec(tempC);
  if (!addSec || !plan || !Array.isArray(plan.children)) return null;
  const current = queue?.[currentIndex];
  if (!current) return null;

  // The warm-up is the root block the session is in now.
  const rootIdx = Number(current.sourceTreePath?.[1]);
  if (!Number.isInteger(rootIdx) || !isWarmupBlock(plan.children[rootIdx])) {
    return null;
  }

  // Lengthen the first timed step from here on that runs once: one inside a
  // repeating group would add the time on every round.
  const target = queue.slice(currentIndex).find(
    (item) =>
      Number(item.sourceTreePath?.[1]) === rootIdx &&
      item.kind === "active" &&
      !item.isTransition &&
      isTimedUnit(item.node?.unit) &&
      (item.enclosingGroup?.iterations ?? 1) <= 1,
  );

  const next = clone(plan);
  if (target) {
    const node = nodeAt(next, target.sourceTreePath);
    const fromSec = toSeconds(node.value, node.unit);
    const toSec = fromSec + addSec;
    if (String(node.unit).toLowerCase().startsWith("m") && toSec % 60 === 0) {
      node.value = toSec / 60;
      node.unit = "m";
    } else {
      node.value = toSec;
      node.unit = "s";
    }
    const label = node.label || node.exercise_name || "the warm-up";
    return {
      plan: next,
      addSec,
      text: `${label}: ${minutesText(fromSec)} → ${minutesText(toSec)}`,
    };
  }

  // Nothing timed left to lengthen: add an easy step after the warm-up block.
  next.children.splice(rootIdx + 1, 0, {
    id: `cold-warmup-${Date.now()}`,
    type: "Warmup",
    label: "Easy cardio (extra warm-up)",
    value: addSec / 60,
    unit: "m",
    autoStart: true,
    intensityTarget: "Easy, build gradually",
  });
  return {
    plan: next,
    addSec,
    text: `${minutesText(addSec)} of easy cardio after the warm-up`,
  };
};
